import { useParams, Link } from "wouter";
import { useGetNewsById, useGetEvents, useGetDeals } from "@workspace/api-client-react";
import { useSEO } from "@/hooks/use-seo";
import FeedAdSlot from "@/components/FeedAdSlot";
import { ShareBar } from "@/components/ShareBar";
import { GoogleAd } from "@/components/GoogleAd";
import { QueryState, isNotFoundError } from "@/components/QueryState";
import { ExternalLink, Calendar, Tag } from "lucide-react";

function sanitizeHtml(raw: string): string {
  const doc = new DOMParser().parseFromString(raw, "text/html");
  doc.querySelectorAll("script,embed,object,link,style,base,meta,form").forEach(el => el.remove());
  doc.querySelectorAll("*").forEach(el => {
    Array.from(el.attributes).forEach(a => {
      const val = el.getAttribute(a.name) ?? "";
      if (a.name.startsWith("on")) { el.removeAttribute(a.name); return; }
      if (["href","src","action","formaction","data","xlink:href"].includes(a.name) && /^(javascript|vbscript|data):/i.test(val.trim())) {
        el.removeAttribute(a.name);
      }
    });
    if (el instanceof HTMLElement) el.removeAttribute("style");
  });
  return doc.body.innerHTML;
}

const APP_URL = "https://www.ayrshirehub.co.uk";

type NewsData = {
  id: number;
  slug?: string | null;
  title: string;
  summary: string;
  content?: string | null;
  category?: string | null;
  town?: string | null;
  imageUrl?: string | null;
  sourceName?: string | null;
  sourceLink?: string | null;
  featured?: boolean | null;
  breaking?: boolean | null;
  viewCount?: number | null;
  createdAt: string;
  contactName?: string | null;
  authorName?: string | null;
  byline?: string | null;
};

function isHtml(str: string) {
  return /<[a-z][\s\S]*>/i.test(str);
}

function ArticleSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="h-72 bg-slate-200 animate-pulse w-full" />
      <div className="container mx-auto px-4 max-w-2xl py-8 space-y-4">
        <div className="h-5 bg-slate-200 rounded animate-pulse w-32" />
        <div className="h-10 bg-slate-200 rounded animate-pulse" />
        <div className="h-10 bg-slate-200 rounded animate-pulse w-4/5" />
        <div className="h-4 bg-slate-200 rounded animate-pulse w-48 mt-2" />
        <div className="space-y-3 mt-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-4 bg-slate-200 rounded animate-pulse" style={{ width: `${85 + (i % 3) * 5}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

type EventItem = { id: number; slug?: string | null; name: string; imageUrl?: string | null; town?: string | null; venue?: string | null; date?: string | null; price?: string | null };
type DealItem  = { id: number; slug?: string | null; title: string; imageUrl?: string | null; businessName?: string | null; endDate?: string | null };

function EventPromoCard({ event }: { event: EventItem }) {
  const dateStr = event.date ? new Date(event.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : null;
  return (
    <Link href={`/events/${event.slug ?? event.id}`}>
      <div className="flex gap-3 p-3 rounded-xl border border-slate-200 hover:border-primary/40 hover:bg-primary/[0.02] transition-colors cursor-pointer group">
        {event.imageUrl ? (
          <img src={event.imageUrl} alt={event.name} className="w-16 h-16 rounded-lg object-cover shrink-0 bg-slate-100" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-primary/10 shrink-0 flex items-center justify-center">
            <Calendar size={22} className="text-primary/50" />
          </div>
        )}
        <div className="min-w-0">
          <p className="font-semibold text-slate-800 text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">{event.name}</p>
          {dateStr && <p className="text-xs text-primary font-medium mt-0.5">{dateStr}</p>}
          {(event.venue ?? event.town) && <p className="text-xs text-slate-400 mt-0.5 truncate">{event.venue ? `${event.venue}, ${event.town}` : event.town}</p>}
          {event.price && event.price !== "0" && <p className="text-xs text-slate-500 mt-0.5">{event.price === "Free" ? "🎟 Free" : `🎟 ${event.price}`}</p>}
        </div>
      </div>
    </Link>
  );
}

function DealPromoCard({ deal }: { deal: DealItem }) {
  const untilStr = deal.endDate ? new Date(deal.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;
  return (
    <Link href={`/deals/${deal.slug ?? deal.id}`}>
      <div className="flex gap-3 p-3 rounded-xl border border-slate-200 hover:border-amber-400/60 hover:bg-amber-50 dark:bg-amber-900/20/40 transition-colors cursor-pointer group">
        {deal.imageUrl ? (
          <img src={deal.imageUrl} alt={deal.title} className="w-16 h-16 rounded-lg object-cover shrink-0 bg-slate-100" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-amber-50 dark:bg-amber-900/20 shrink-0 flex items-center justify-center">
            <Tag size={20} className="text-amber-400" />
          </div>
        )}
        <div className="min-w-0">
          {deal.businessName && <p className="text-xs font-semibold text-slate-400 mb-0.5 truncate">{deal.businessName}</p>}
          <p className="font-semibold text-slate-800 text-sm leading-snug line-clamp-2 group-hover:text-amber-600 transition-colors">{deal.title}</p>
          {untilStr && <p className="text-xs text-slate-400 mt-0.5">Until {untilStr}</p>}
        </div>
      </div>
    </Link>
  );
}

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: rawData, isLoading, isError, error, refetch } = useGetNewsById(id as unknown as number);
  const data = rawData as NewsData | undefined;
  const { data: eventsRaw } = useGetEvents({ limit: 3 });
  const { data: dealsRaw } = useGetDeals({ limit: 3 });
  const events = (eventsRaw as { items: EventItem[] } | undefined)?.items ?? [];
  const deals  = (dealsRaw  as { items: DealItem[] }  | undefined)?.items ?? [];

  const authorDisplay = data?.byline ?? data?.contactName ?? data?.authorName ?? null;
  const canonicalUrl = data ? `${APP_URL}/news/${data.slug ?? data.id}` : APP_URL;

  useSEO({
    title: data?.title ?? undefined,
    description: data?.summary ?? undefined,
    image: data?.imageUrl ?? undefined,
    type: "article",
    jsonLd: data ? {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": data.title,
      "description": data.summary,
      "datePublished": data.createdAt,
      "url": canonicalUrl,
      "image": data.imageUrl ?? undefined,
      "publisher": {
        "@type": "Organization",
        "name": "Ayrshire Hub",
        "url": APP_URL,
        "logo": { "@type": "ImageObject", "url": `${APP_URL}/opengraph.jpg` },
      },
      ...(authorDisplay ? { "author": { "@type": "Person", "name": authorDisplay } } : {}),
    } : undefined,
  });

  // 404 = the article was pulled or the slug is wrong — retrying won't fix it,
  // so keep the "not found" card. Everything else gets a retry.
  const isMissing = !isLoading && (isNotFoundError(error) || (!isError && !data));

  if (isLoading || (isError && !isMissing)) {
    return (
      <QueryState
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        skeleton={<ArticleSkeleton />}
        errorTitle="Couldn't load this article"
        errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
        errorAction={{ label: "Back to News", href: "/news" }}
        fullPage
      />
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-5xl mb-4">📰</p>
        <p className="text-slate-500 font-medium">Article not found.</p>
        <Link href="/news">
          <button className="mt-4 px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Back to News</button>
        </Link>
      </div>
    );
  }

  const pubDate = new Date(data.createdAt);
  const dateStr = pubDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeStr = pubDate.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const contentHtml = data.content && data.content.trim() !== "" ? data.content : null;
  const isRichContent = contentHtml ? isHtml(contentHtml) : false;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero image — full bleed */}
      {data.imageUrl && (
        <div className="w-full flex justify-center bg-slate-900">
          <img
            src={data.imageUrl}
            alt={data.title}
            style={{ display: "block", width: "auto", height: "auto", maxHeight: 480, maxWidth: "100%" }}
          />
        </div>
      )}

      <div className="container mx-auto px-4 max-w-2xl py-8">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-slate-400 mb-6">
          <Link href="/" className="hover:text-primary transition-colors">Home</Link>
          <span aria-hidden="true">/</span>
          <Link href="/news" className="hover:text-primary transition-colors">News</Link>
          <span aria-hidden="true">/</span>
          <span className="text-slate-600 truncate max-w-[200px]" aria-current="page">{data.title}</span>
        </nav>

        <article>
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {data.breaking && (
              <span className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Breaking
              </span>
            )}
            {data.featured && (
              <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                ★ Featured
              </span>
            )}
            {data.category && (
              <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full capitalize">
                {data.category}
              </span>
            )}
            {data.town && (
              <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2.5 py-1 rounded-full">
                📍 {data.town}
              </span>
            )}
          </div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground leading-tight mb-4">
            {data.title}
          </h1>

          {/* Byline row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-400 mb-6 pb-6 border-b border-slate-200">
            {authorDisplay && (
              <>
                <span className="font-semibold text-slate-600">By {authorDisplay}</span>
                <span className="text-slate-300">·</span>
              </>
            )}
            <span>{dateStr}</span>
            <span className="text-slate-300">·</span>
            <span>{timeStr}</span>
            {(data.viewCount ?? 0) > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span>{(data.viewCount ?? 0).toLocaleString()} {data.viewCount === 1 ? "read" : "reads"}</span>
              </>
            )}
          </div>

          {/* Lead paragraph */}
          <p className="text-lg sm:text-xl text-slate-700 font-medium leading-relaxed mb-8">
            {data.summary}
          </p>

          {/* Ad — after lead paragraph */}
          <div className="my-6">
            <GoogleAd />
          </div>

          {/* Article body */}
          {contentHtml && contentHtml !== data.summary && (
            isRichContent ? (
              <div
                className="mb-8 article-body"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(contentHtml) }}
              />
            ) : (
              <div className="mb-8 text-slate-700 leading-relaxed whitespace-pre-wrap text-base">
                {contentHtml}
              </div>
            )
          )}

          {/* In-article advert — text-only so it reads as part of the story */}
          <div className="mb-8"><FeedAdSlot placement="news" /></div>

          {/* Events & Deals promo — shown between article body and source */}
          {(events.length > 0 || deals.length > 0) && (
            <div className="mb-8 space-y-5">
              {events.length > 0 && (
                <div className="rounded-2xl border border-primary/20 bg-primary/[0.03] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar size={15} className="text-primary" />
                      <span className="text-xs font-bold text-primary uppercase tracking-wider">What's on in Ayrshire</span>
                    </div>
                    <Link href="/events" className="text-xs font-semibold text-primary hover:underline">See all →</Link>
                  </div>
                  <div className="space-y-2">
                    {events.map(e => <EventPromoCard key={e.id} event={e} />)}
                  </div>
                </div>
              )}

              {deals.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20/40 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Tag size={15} className="text-amber-600" />
                      <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Local Deals & Discounts</span>
                    </div>
                    <Link href="/deals" className="text-xs font-semibold text-amber-600 hover:underline">See all →</Link>
                  </div>
                  <div className="space-y-2">
                    {deals.map(d => <DealPromoCard key={d.id} deal={d} />)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Source attribution */}
          {data.sourceName && (
            <div className="mb-8 p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
              <div className="w-1 self-stretch rounded bg-primary/30 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Source</p>
                {data.sourceLink ? (
                  <a
                    href={data.sourceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {data.sourceName}
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <p className="text-sm font-semibold text-primary">{data.sourceName}</p>
                )}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-2 pt-6 border-t border-slate-200">
            <ShareBar url={canonicalUrl} title={data.title} />
          </div>

          <div className="mt-6">
            <Link href="/news" className="text-sm text-slate-400 hover:text-primary transition-colors">
              ← More news from Ayrshire Hub
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
